import { defineStore } from "pinia";

import type { PiniaStore, ReachableStore } from "../shared";
import * as agentMemory from "./agentMemory";
import * as agentSchedule from "./agentSchedule";
import * as attachment from "./attachment";
import * as automation from "./automation";
import * as chat from "./chat";
import * as chatMessage from "./chatMessage";
import * as comment from "./comment";
import * as connector from "./connector";
import * as dartboard from "./dartboard";
import * as doc from "./doc";
import * as event from "./event";
import * as form from "./form";
import * as helpers from "./helpers";
import * as notification from "./notification";
import * as option from "./option";
import * as paginationState from "./paginationState";
import * as property from "./property";
import * as relationship from "./relationship";
import * as skill from "./skill";
import * as space from "./space";
import * as status from "./status";
import * as task from "./task";
import * as taskKind from "./taskKind";
import * as textSnapshot from "./textSnapshot";
import * as user from "./user";
import * as view from "./view";
import * as webhook from "./webhook";

type State = agentMemory.State &
  agentSchedule.State &
  automation.State &
  attachment.State &
  chat.State &
  chatMessage.State &
  comment.State &
  connector.State &
  dartboard.State &
  doc.State &
  event.State &
  form.State &
  helpers.State &
  notification.State &
  option.State &
  paginationState.State &
  property.State &
  relationship.State &
  skill.State &
  space.State &
  status.State &
  task.State &
  taskKind.State &
  textSnapshot.State &
  user.State &
  view.State &
  webhook.State;

type Getters = agentMemory.Getters &
  agentSchedule.Getters &
  automation.Getters &
  attachment.Getters &
  chat.Getters &
  chatMessage.Getters &
  comment.Getters &
  connector.Getters &
  dartboard.Getters &
  doc.Getters &
  event.Getters &
  form.Getters &
  helpers.Getters &
  notification.Getters &
  option.Getters &
  paginationState.Getters &
  property.Getters &
  relationship.Getters &
  skill.Getters &
  space.Getters &
  status.Getters &
  task.Getters &
  taskKind.Getters &
  textSnapshot.Getters &
  user.Getters &
  view.Getters &
  webhook.Getters;

type Actions = agentMemory.Actions &
  agentSchedule.Actions &
  automation.Actions &
  attachment.Actions &
  chat.Actions &
  chatMessage.Actions &
  comment.Actions &
  connector.Actions &
  dartboard.Actions &
  doc.Actions &
  event.Actions &
  form.Actions &
  helpers.Actions &
  notification.Actions &
  option.Actions &
  paginationState.Actions &
  property.Actions &
  relationship.Actions &
  skill.Actions &
  space.Actions &
  status.Actions &
  task.Actions &
  taskKind.Actions &
  textSnapshot.Actions &
  user.Actions &
  view.Actions &
  webhook.Actions;

type Name = "DataStore";
export type DataStore = PiniaStore<Name, State, Getters, Actions>;

const useStore: ReachableStore<Name, State, Getters, Actions> = defineStore("DataStore", {
  state: (): State => ({
    ...agentMemory.state,
    ...agentSchedule.state,
    ...attachment.state,
    ...automation.state,
    ...chat.state,
    ...chatMessage.state,
    ...comment.state,
    ...connector.state,
    ...dartboard.state,
    ...doc.state,
    ...event.state,
    ...form.state,
    ...helpers.state,
    ...notification.state,
    ...option.state,
    ...paginationState.state,
    ...property.state,
    ...relationship.state,
    ...skill.state,
    ...space.state,
    ...status.state,
    ...task.state,
    ...taskKind.state,
    ...textSnapshot.state,
    ...user.state,
    ...view.state,
    ...webhook.state,
  }),
  undo: true,
  getters: {
    ...agentMemory.getters,
    ...agentSchedule.getters,
    ...attachment.getters,
    ...automation.getters,
    ...chat.getters,
    ...chatMessage.getters,
    ...comment.getters,
    ...connector.getters,
    ...dartboard.getters,
    ...doc.getters,
    ...event.getters,
    ...form.getters,
    ...helpers.getters,
    ...notification.getters,
    ...option.getters,
    ...paginationState.getters,
    ...property.getters,
    ...relationship.getters,
    ...skill.getters,
    ...space.getters,
    ...status.getters,
    ...task.getters,
    ...taskKind.getters,
    ...textSnapshot.getters,
    ...user.getters,
    ...view.getters,
    ...webhook.getters,
  },
  actions: {
    ...agentMemory.actions,
    ...agentSchedule.actions,
    ...attachment.actions,
    ...automation.actions,
    ...chat.actions,
    ...chatMessage.actions,
    ...comment.actions,
    ...connector.actions,
    ...dartboard.actions,
    ...doc.actions,
    ...event.actions,
    ...form.actions,
    ...helpers.actions,
    ...notification.actions,
    ...option.actions,
    ...paginationState.actions,
    ...property.actions,
    ...relationship.actions,
    ...skill.actions,
    ...space.actions,
    ...status.actions,
    ...task.actions,
    ...taskKind.actions,
    ...textSnapshot.actions,
    ...user.actions,
    ...view.actions,
    ...webhook.actions,
  },
});

export default useStore;
